import React, { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouteMatch } from 'react-router';
import moment from 'moment';

import * as AnalyticsService from '@common/services/analytics';
import Profile from '@common/components/profile';
import Card from '@common/components/card';
import Icon from '@common/components/icon';
import Text from '@common/components/text';
import Permission from '@common/components/permission';
import Dropdown from '@common/components/dropdown';
import Attachment from '@common/components/attachment';
import ConfirmButton from '@common/components/confirm-button';
import Attachments from '../attachments';
import EditForm from '../../../../forms/edit-message';
import Files from '../files';
import Poll from '../../../poll';
import { EParentTypes, EComponentTypes } from '@common/definitions';
import type { LoggedUser } from '@common/types/objects';
import type { Message } from '../../../../types/objects';
import Tooltip from '@common/components/tooltip';
import { Link } from 'react-router-dom';
import LinkWrapper from '@common/components/linkWrapper';

const { EEventNames } = AnalyticsService;

type Props = {
  loggedUser: LoggedUser;
  item: Message;
  shouldHideParent: boolean;
  disableActions: boolean;
  onVotePoll: (pollId: string, vote: boolean, optionId: string, optionIds: string[], messageId?: string) => void;
  onDelete: (id: string) => Promise<void>;
  onToggleImportant: (id: string, important: boolean) => Promise<void>;
  onToggleComments: (messageId: string, enabled: boolean) => Promise<void>;
  onToggleMute: (id: string) => Promise<void>;
  translateMessage: (id: string) => Promise<void>;
  onMarkAsRead: () => void;
};

const DefaultMessageContentComponent = ({
  item,
  loggedUser,
  shouldHideParent,
  disableActions,
  onVotePoll,
  translateMessage,
  onMarkAsRead,
  onToggleImportant,
  onToggleComments,
  onToggleMute,
  onDelete,
}: Props) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [showTranslation, setShowTranslation] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const networkId = item.created_in?.type === EParentTypes.TEAM ?
  item.created_in?.parent?.networkId as string : item.created_in?.id;

  const toggleTranslation = async () => {
    if (showTranslation) {
      setShowTranslation(false);
    } else {
      if (!item.translated_text || !item.translated_text[loggedUser.translation_language.locale]) {
        setIsTranslating(true);

        await translateMessage(item.id);
      }

      setIsTranslating(false);
      setShowTranslation(true);
    }
  };

  // @ts-expect-error
  const { user, attachments, videos, files } = item;

  const showDropdown = item.actions?.can_edit
    || item.actions?.can_mute
    || item.actions?.can_mark_important
    || item.actions?.can_delete;

  const match = useRouteMatch();
  const isChannel = match.path === '/networks/:networkId/messages/:type/:id' &&
    // @ts-expect-error
    match?.params?.type === 'channel';

  const createContent = () => {
    if (isEditing) {
      return (
        <EditForm
          form={`edit-message/${item.id}`}
          message={item}
          onClose={() => setIsEditing(false)}
          hasAttachments={!!(attachments?.length || files?.length || videos?.length)}
        />
      );
    }

    const { poll } = item;
    const text = (
      (loggedUser.translation_language && showTranslation && item.translated_text)
        && item.translated_text[loggedUser.translation_language.locale]
    ) || item.text;

    const canTranslate = item.content_language
      && loggedUser.translation_language
      && item.content_language !== loggedUser.translation_language.locale;

    return (
      <>
        {typeof text === 'string' && <Text enableReadMore>{text}</Text>}
        <Permission component={EComponentTypes.FEED_TRANSLATION}>
          {canTranslate && (
            <div className="Message__Translate" onClick={toggleTranslation} role="button">
              {showTranslation
                ? t('social:feed_item_show_original')
                : t(isTranslating ? 'social:feed_item_translating' : 'social:feed_item_show_translation')}
            </div>
          )}
        </Permission>
        {poll && (
          <div className="Message__content">
            <Poll
              poll={poll}
              message={item}
              onVote={disableActions ? undefined : onVotePoll}
            />
          </div>
        )}
      </>
    );
  };

  const getIconDisplay = () => {
    switch (item.created_in?.type) {
      case EParentTypes.CHANNEL:
      case EParentTypes.ORGANISATION:
        return (
          <div className="icon-active primary tw-rounded-full  tw-p-[3px] tw-absolute tw-right-[-5px] tw-bottom-0">
            <Icon key="icon" type="bag" className="!tw-w-2.5 !tw-h-2.5  tw-text-white" />
          </div>);
      case EParentTypes.NETWORK:
      case EParentTypes.TEAM:
        return (
          <div className="icon-active tw-rounded-full  tw-p-[3px] tw-absolute tw-right-[-5px] tw-bottom-0">
            <Icon key="icon" type="community_icon" className="!tw-w-2.5 !tw-h-2.5 tw-text-white" />
          </div>);
      case EParentTypes.EVENT:
        return (
          <div className="icon-active event-icon tw-rounded-full  tw-p-[3px] tw-absolute tw-right-[-5px] tw-bottom-0">
            <Icon key="icon" type="calendar_today" className="!tw-w-2.5 !tw-h-2.5 tw-text-white" />
          </div>);
        default:
        return null;
    }
  };
 const defaultNetworkId = item.created_in?.type === EParentTypes.TEAM ? item.created_in?.parent?.networkId as string :
    // @ts-expect-error
    match?.params?.networkId;

  const subHeaderLink = item.created_in?.type === EParentTypes.EVENT ?
  `/networks/${defaultNetworkId}/events/${item.created_in?.id}`
  : `/networks/${defaultNetworkId}/messages/${item.created_in?.type}/${item.created_in?.id}`;

  const WrapLinkComponent = (children:ReactNode) => (
    <LinkWrapper
      className="tw-text-inherit hover:tw-text-inherit"
      url={'/messages/' + item.id}
    >{children}
    </LinkWrapper>);
 return (
   <div className="Message__Inner Message--type-default">
     <Card.Header>
       <div className="Message__header__inner">
         <div className="pull-right">
           {item.is_important && <Icon type="star__filled" className="Message__Important" />}
           {showDropdown && (
           <Dropdown
             alignRight
             id={`Message__Actions-${item.id}`}
             toggle={<Icon type="more_horiz" size="small" />}
           >
             {item.actions.can_edit && (
             <Dropdown.Item onClick={() => setIsEditing(true)}>
               {t('social:feed_item_message_edit_message')}
             </Dropdown.Item>
                )}
             {item.is_important && !item.has_read && (
             <Dropdown.Item onClick={onMarkAsRead}>
               {t('social:feed_item_message_mark_as_read')}
             </Dropdown.Item>
                )}
             {!item.publish_at && item.actions.can_mute && (
             <Dropdown.Item onClick={() => onToggleMute(item.id)}>
               {!item.has_muted
                      ? t('social:feed_item_message_mute')
                      : t('social:feed_item_message_unmute')}
             </Dropdown.Item>
                )}
             {!item.publish_at && item.actions.can_mark_important && (item.is_important ? (
               <ConfirmButton
                 danger
                 key="remove"
                 title={t('social:feed_item_message_default_confirm_remove_important_title')}
                 onConfirm={() => onToggleImportant(item.id, false)}
               >
                 <Dropdown.Item>{t('social:feed_item_message_remove_important')}</Dropdown.Item>
               </ConfirmButton>
                ) : (
                  <ConfirmButton
                    key="mark"
                    title={t('social:feed_item_message_default_confirm_mark_as_important_title')}
                    onConfirm={() => onToggleImportant(item.id, true)}
                  >
                    <Dropdown.Item>{t('social:feed_item_message_mark_as_important')}</Dropdown.Item>
                  </ConfirmButton>
                ))}
             {item.actions.can_disable_comments && (item.can_comment ? (
               <ConfirmButton
                 danger
                 key="disable_comments"
                 title={t('social:feed_item_message_default_confirm_disable_comments_title')}
                 onConfirm={() => onToggleComments(item.id, false)}
               >
                 <Dropdown.Item>{t('social:feed_item_message_disable_comments')}</Dropdown.Item>
               </ConfirmButton>
                ) : (
                  <ConfirmButton
                    key="enable_comments"
                    title={t('social:feed_item_message_default_confirm_enable_comments_title')}
                    onConfirm={() => onToggleComments(item.id, true)}
                  >
                    <Dropdown.Item>{t('social:feed_item_message_enable_comments')}</Dropdown.Item>
                  </ConfirmButton>
                ))}
             {item.actions.can_delete && (
             <ConfirmButton
               danger
               title={t('social:feed_item_message_default_confirm_remove_title')}
               description={t('social:feed_item_message_default_confirm_remove_description')}
               onConfirm={() => onDelete(item.id)}
               confirmText={t('social:feed_item_message_default_confirm_remove')}
             >
               <Dropdown.Item danger>{t('social:feed_item_message_default_confirm_remove')}</Dropdown.Item>
             </ConfirmButton>
                )}
           </Dropdown>
            )}
         </div>

         <Profile
           user={user}
           meta={!shouldHideParent && (
           <>
             {item.created_in?.type !== EParentTypes.NETWORK && item.created_in?.type !== EParentTypes.ORGANISATION &&
             <>
               <Icon type="chevron_right" />
               <LinkWrapper
                 url={subHeaderLink}
                 className="tw-text-inherit hover:tw-text-inherit"
               >{item.created_in?.values}
               </LinkWrapper>
             </>}
           </>
            )}
           enablePopover={item.user_id !== loggedUser.id && !isChannel}
           showExtraIcon={getIconDisplay()}
           nameWrapper={WrapLinkComponent}
         >
           <div>
             <small className={item.publish_at && 'Message__PublishAt'}>
               {(item.created_in?.type === EParentTypes.TEAM || item.created_in?.type === EParentTypes.NETWORK) && (
               <Link
                 to={`/networks/${networkId}/messages/network`}
                 className="message-meta-item network tw-cursor-pointer"
               >
                 {item.created_in?.parent?.networkName || item.created_in?.parent?.name || ''}
               </Link>
              )}
               {(item.created_in?.type === EParentTypes.CHANNEL) && (
               <span
                 className="message-meta-item organisation"
               >
                 {item.created_in?.parent?.organisationName}
               </span>
              )}
               {item.created_in?.type === EParentTypes.EVENT && (
               <Link
                 to={`/networks/${defaultNetworkId}/events`}
                 className="message-meta-item event"
               >
                 {t('social:event')}
               </Link>
              )}
               <span className="!tw-mr-0">·</span>
               <Tooltip title={item?.created_at ? moment(item.created_at).format('LLLL') : ''}>
                 <span>
                   <Icon key="icon" type="access_time" />
                   {item?.publish_at ? (
                    t('social:feed_item_message_default_planned_at', {
                      datetime: moment(item.publish_at).format(`dddd D MMMM [${t('social:feed_item_message_default_at')}] LT`),
                    })
                  ) : item?.created_at ? moment(item.created_at).calendar() : ''}
                 </span>
               </Tooltip>
             </small>
           </div>
         </Profile>
       </div>
     </Card.Header>

     <div className="Message__content fs-exclude">
       {createContent()}
     </div>
     {/* @ts-expect-error */}
     {videos.map((video) => (
       <div key={video.id} className="Message__content">
         <Attachment
           item={video}
           onOpen={() => AnalyticsService.track(EEventNames.OPENED_VIDEO)}
         />
       </div>
      ))}
     <Attachments items={attachments} message={item} />
     <Files items={files} message={item} />
   </div>
  );
};

export default DefaultMessageContentComponent;
